/**
 * Card Management Component
 * Manage Marqeta Issuing cards with spending limits and merchant restrictions
 * Trust Marketplace - Phase 3: Payment Infrastructure (Marqeta Issuing)
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { CapabilityGate } from '../common/CapabilityGate';
import { getWeaveinApiUrl } from '@/src/config';
import { marqetaCardService, MarqetaCardResponse, MERCHANT_CATEGORIES } from '@/services/marqeta/cardIssuance';
import { CardIssuer } from '../financial/CardIssuer';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  CreditCard,
  Plus,
  Eye,
  Lock,
  Unlock,
  Ban,
  DollarSign,
  ShoppingBag,
  Calendar,
  AlertCircle,
  CheckCircle,
  Users,
  Filter
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface CardManagementProps {
  memberId?: number;
  cboId?: number;
}

interface Member {
  id: number;
  member_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  cbo_id: number;
}

const CARD_TYPES = [
  { value: 'VIRTUAL', label: 'Virtual Card' },
  { value: 'PHYSICAL', label: 'Physical Card' }
];

const CARD_STATUSES = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

const SPENDING_INTERVALS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUAL', label: 'Annual' }
];

const MERCHANT_CATEGORY_OPTIONS = [
  { value: 'GROCERIES', label: 'Groceries' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'GAS_STATIONS', label: 'Gas Stations' },
  { value: 'RESTAURANTS', label: 'Restaurants' },
  { value: 'RETAIL', label: 'Retail' }
];

const CardManagementComponent: React.FC<CardManagementProps> = ({ memberId, cboId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [cards, setCards] = useState<MarqetaCardResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Members for selection
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');

  // Modal states
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isCardIssuerOpen, setIsCardIssuerOpen] = useState(false);
  const [selectedMemberForCard, setSelectedMemberForCard] = useState<{ id: number, name: string } | null>(null);
  const [selectedCard, setSelectedCard] = useState<MarqetaCardResponse | null>(null);
  const [isLimitsModalOpen, setIsLimitsModalOpen] = useState(false);

  // Issue card form
  const [newCard, setNewCard] = useState({
    member_id: memberId || 0,
    card_type: 'VIRTUAL' as 'VIRTUAL' | 'PHYSICAL',
    spending_limit_amount: '1000',
    spending_limit_interval: 'MONTHLY',
    allowed_categories: [] as string[],
    blocked_categories: [] as string[]
  });

  // Update limits form
  const [limitsForm, setLimitsForm] = useState({
    spending_limit_amount: '',
    spending_limit_interval: '',
    allowed_categories: [] as string[],
    blocked_categories: [] as string[]
  });

  useEffect(() => {
    fetchCards();
    if (!memberId) {
      fetchMembers();
    }
  }, [memberId, cboId, filterStatus, filterType]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('page_size', '500');

      const url = getWeaveinApiUrl(`/api/members/?${params.toString()}`);

      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        const membersList = data.members || [];
        setMembers(membersList);
      } else {
        console.error('Failed to fetch members:', response.status);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchCards = async () => {
    try {
      setLoading(true);
      let fetchedCards: MarqetaCardResponse[] = [];

      if (memberId) {
        fetchedCards = await marqetaCardService.getMemberCards(memberId, filterStatus || undefined);
      } else {
        // If no memberId, we might want to fetch all cards (admin view)
        // Currently using the same endpoint but we might need a different one for all cards
        // For now, falling back to the direct fetch for "all cards" if service doesn't support it yet
        const params = new URLSearchParams();
        if (filterStatus) params.append('status', filterStatus);
        if (filterType) params.append('card_type', filterType);

        const url = getWeaveinApiUrl(`/api/issued-cards?${params.toString()}`);
        const response = await authFetch(url);

        if (!response.ok) throw new Error('Failed to fetch cards');
        fetchedCards = await response.json();
      }

      setCards(fetchedCards);
      setError(null);
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const issueCard = async () => {
    try {
      const targetMemberId = memberId || selectedMember?.id || newCard.member_id;
      if (!targetMemberId) {
        alert('Please select a member');
        return;
      }

      await marqetaCardService.issueCard({
        memberId: targetMemberId,
        cardType: newCard.card_type,
        cardholderName: selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : 'Member',
        loadAmount: parseFloat(newCard.spending_limit_amount),
        spendingLimits: {
          monthly: parseFloat(newCard.spending_limit_amount)
        },
        allowedCategories: newCard.allowed_categories
      });

      setNewCard({
        member_id: memberId || 0,
        card_type: 'VIRTUAL',
        spending_limit_amount: '1000',
        spending_limit_interval: 'MONTHLY',
        allowed_categories: [],
        blocked_categories: []
      });
      setIsIssueModalOpen(false);
      fetchCards();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to issue card');
    }
  };

  const updateCardLimits = async () => {
    if (!selectedCard) return;

    try {
      await marqetaCardService.updateSpendingControls(selectedCard.id, {
        transaction_limit: parseFloat(limitsForm.spending_limit_amount),
        allowed_categories: limitsForm.allowed_categories
      });

      setIsLimitsModalOpen(false);
      setSelectedCard(null);
      fetchCards();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update limits');
    }
  };

  const cancelCard = async (card: MarqetaCardResponse) => {
    if (!confirm(`Are you sure you want to cancel card ending in ${card.card_number_last4}?`)) {
      return;
    }

    try {
      // Marqeta service doesn't have cancel exposed yet, using direct fetch or suspend
      // Using suspend for now as it's safer, or call the backend cancel endpoint
      const url = getWeaveinApiUrl(`/api/issued-cards/${card.id}/cancel?reason=Admin%20Request`);

      const response = await authFetch(url, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to cancel card');

      alert('Card cancelled successfully');
      fetchCards();
    } catch (err) {
      console.error('Error cancelling card:', err);
      alert(err instanceof Error ? err.message : 'Failed to cancel card');
    }
  };

  const openLimitsModal = (card: MarqetaCardResponse) => {
    setSelectedCard(card);
    setLimitsForm({
      spending_limit_amount: (card.current_balance || 0).toString(), // Using balance as proxy
      spending_limit_interval: 'MONTHLY',
      allowed_categories: [], // Would need to fetch these details
      blocked_categories: []
    });
    setIsLimitsModalOpen(true);
  };

  const toggleCategory = (category: string, type: 'allowed' | 'blocked') => {
    const field = type === 'allowed' ? 'allowed_categories' : 'blocked_categories';
    const current = limitsForm[field];
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    setLimitsForm({ ...limitsForm, [field]: updated });
  };

  const getStatusColor = (status: string | undefined | null): string => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (status.toUpperCase()) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'CANCELLED': return 'bg-red-100 text-red-800 border-red-200';
      case 'SUSPENDED': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return fmt.medium(dateString);
  };

  // Summary metrics
  const totalCards = cards.length;
  const activeCards = cards.filter(c => c.status === 'ACTIVE').length;
  const totalSpendingLimit = cards.reduce((sum, c) => sum + (c.current_balance || 0), 0); // Using balance as proxy
  const totalSpent = 0; // Need transaction data for this

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('payments.card_management', 'Card Management')}</h2>}
          <p className="text-sm text-gray-600 mt-1">Manage member prepaid cards (Marqeta)</p>
        </div>

        <Dialog open={isIssueModalOpen} onOpenChange={(open) => {
          setIsIssueModalOpen(open);
          if (!open) {
            setSelectedMember(null);
            setSearchQuery('');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-v2accent hover:bg-v2accent/90">
              <Plus className="w-4 h-4 mr-2" />
              {t('payments.issue_card', 'Issue Card')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('payments.issue_new_card', 'Issue New Card')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Member Selection */}
              {!memberId && (
                <div>
                  <Label htmlFor="member_select">
                    {t('payments.select_member', 'Select Member')} <span className="text-red-600">*</span>
                  </Label>
                  <div className="mt-2">
                    {loadingMembers ? (
                      <div className="flex items-center justify-center p-4">
                        <LoadingSpinner />
                        <span className="ml-2 text-sm text-gray-600">{t('payments.loading_members', 'Loading members...')}</span>
                      </div>
                    ) : (
                      <>
                        <Input
                          placeholder={t('payments.search_members_by_name_email', 'Search members by name, email, or member #...')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="mb-2"
                        />
                        <Select
                          value={selectedMember?.id.toString() || ''}
                          onValueChange={(value) => {
                            const member = members.find(m => m.id.toString() === value);
                            setSelectedMember(member || null);
                            setNewCard({ ...newCard, member_id: member ? member.id : 0 });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('payments.choose_a_member_to_issue', 'Choose a member to issue card')} />
                          </SelectTrigger>
                          <SelectContent position="popper" className="max-h-64 z-[100]" sideOffset={5}>
                            {members.length === 0 ? (
                              <div className="p-4 text-sm text-gray-500 text-center">
                                {t('payments.no_members_found', 'No members found')}
                              </div>
                            ) : (
                              members
                                .filter(m => {
                                  if (!searchQuery) return true;
                                  const query = searchQuery.toLowerCase().trim();
                                  return `${m.first_name} ${m.last_name}`.toLowerCase().includes(query) ||
                                    (m.email || '').toLowerCase().includes(query);
                                })
                                .map(member => (
                                  <SelectItem key={member.id} value={member.id.toString()}>
                                    {member.first_name} {member.last_name} • {member.member_number}
                                  </SelectItem>
                                ))
                            )}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="card_type">{t('payments.card_type', 'Card Type')}</Label>
                  <Select
                    value={newCard.card_type}
                    onValueChange={(value) => setNewCard({ ...newCard, card_type: value as 'VIRTUAL' | 'PHYSICAL' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CARD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="spending_limit_amount">{t('payments.initial_load_amount', 'Initial Load Amount')}</Label>
                  <Input
                    id="spending_limit_amount"
                    type="number"
                    value={newCard.spending_limit_amount}
                    onChange={(e) => setNewCard({ ...newCard, spending_limit_amount: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsIssueModalOpen(false)}>{t('payments.cancel', 'Cancel')}</Button>
                <Button onClick={issueCard} className="bg-v2accent hover:bg-v2accent/90">
                  {t('payments.issue_card_1', 'Issue Card')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('payments.total_cards', 'Total Cards')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{totalCards}</div>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('payments.active_cards', 'Active Cards')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{activeCards}</div>
        </Card>
        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('payments.total_balance', 'Total Balance')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{formatCurrency(totalSpendingLimit)}</div>
        </Card>
      </div>

      {/* Cards List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map(card => (
          <Card key={card.id} className="overflow-hidden border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="p-5">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${card.card_type === 'VIRTUAL' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{card.cardholder_name}</h3>
                    <p className="text-xs text-gray-500">•••• {card.card_number_last4}</p>
                  </div>
                </div>
                <Badge className={getStatusColor(card.status)}>
                  {card.status}
                </Badge>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('payments.balance', 'Balance')}</span>
                  <span className="font-medium text-gray-900">{formatCurrency(card.current_balance || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('payments.expires', 'Expires')}</span>
                  <span className="font-medium text-gray-900">{formatDate(card.expiration_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('payments.network', 'Network')}</span>
                  <span className="font-medium text-gray-900">{card.card_network}</span>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openLimitsModal(card)}>
                  {t('payments.limits', 'Limits')}
                </Button>
                {card.status === 'ACTIVE' ? (
                  <Button variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => cancelCard(card)}>
                    {t('payments.cancel_1', 'Cancel')}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="flex-1" disabled>
                    {t('payments.cancelled', 'Cancelled')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}

        {cards.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <CreditCard className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t('payments.no_cards_issued_yet', 'No cards issued yet')}</h3>
            <p className="text-gray-500 mt-1 mb-4">{t('payments.issue_a_virtual_or_physical', 'Issue a virtual or physical card to get started')}</p>
            <Button onClick={() => setIsIssueModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('payments.issue_first_card', 'Issue First Card')}
            </Button>
          </div>
        )}
      </div>

      {/* Limits Modal */}
      <Dialog open={isLimitsModalOpen} onOpenChange={setIsLimitsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.update_card_limits', 'Update Card Limits')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="update_limit">{t('payments.spending_limit', 'Spending Limit')}</Label>
              <Input
                id="update_limit"
                type="number"
                value={limitsForm.spending_limit_amount}
                onChange={(e) => setLimitsForm({ ...limitsForm, spending_limit_amount: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsLimitsModalOpen(false)}>{t('payments.cancel_2', 'Cancel')}</Button>
              <Button onClick={updateCardLimits}>{t('payments.save_changes', 'Save Changes')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CardManagementComponent;
